import { memo } from 'react';
import { LABEL_STYLE } from '../shared/constants';
import { InnovationTooltip } from '../shared/InnovationTooltip';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

export const InnovationCrossProgramChart = memo(function InnovationCrossProgramChart() {
  const { crossProgramData, totalCount, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(120);
  if (!crossProgramData || crossProgramData.pairs.length === 0) return (
    <ChartWrapper title="Cross-Program Innovation Sharing">
      <NoChartData message="No cross-program innovation co-contributions" detail="This chart shows which programs co-contribute to each other's innovations using the Contributing Initiatives field. No cross-program sharing was found — all innovation results in the current selection belong to a single program." />
    </ChartWrapper>
  );

  const { pairs, programs } = crossProgramData;

  const topPairs = pairs.slice(0, 15).map(p => ({
    pair: `${p.source} ↔ ${p.target}`,
    fullPair: `${p.sourceName} ↔ ${p.targetName}`,
    sourceName: p.sourceName,
    targetName: p.targetName,
    count: p.count,
  }));

  const exportData = pairs.map(p => ({
    'Program A': p.sourceName,
    'Program B': p.targetName,
    'Joint Innovation Results': p.count,
  }));

  const totalJointResults = pairs.reduce((s, p) => s + p.count, 0);

  return (
    <ChartWrapper title="Cross-Program Innovation Collaboration" info="Cross-program pairs that most frequently co-contribute to innovation results." chartData={exportData} exportFilename="innovation-cross-program" sourceResults={innovationResults}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <div className="bg-indigo-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-700">{programs.length}</p>
          <p className="text-xs text-indigo-600">Programs involved</p>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-purple-700">{pairs.length}</p>
          <p className="text-xs text-purple-600">Unique program pairs</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-700">{totalJointResults}</p>
          <p className="text-xs text-blue-600">Joint innovation results</p>
        </div>
      </div>

      <h5 className="text-sm font-medium text-gray-700 mb-2">Top Cross-Program Collaborations</h5>
      <ResponsiveContainer width="100%" height={Math.max(250, topPairs.length * 28)}>
        <BarChart data={topPairs} layout="vertical" margin={{ left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 12 }} />
          <YAxis type="category" dataKey="pair" tick={{ fontSize: yAxisWidth < 120 ? 8 : 9 }} width={yAxisWidth} />
          <Tooltip content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const d = payload[0].payload;
            return (
              <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4, textAlign: 'center' }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.sourceName}</div>
                <div style={{ color: '#9ca3af', fontSize: '11px' }}>↔</div>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.targetName}</div>
                <div style={{ color: '#6b7280', marginTop: '4px' }}>{d.count.toLocaleString()} joint innovations</div>
              </div>
            );
          }} />
          <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]}>
            <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartWrapper>
  );
});
